import React from "react"
import { graphql } from "gatsby"
import { JsonLd } from "react-schemaorg"
import { Accordion, Block, Container } from "../components"

// Named entities that actually turn up in the DatoCMS answer copy. "&amp;" is
// decoded last so "&amp;mdash;" doesn't get decoded twice.
const NAMED_ENTITIES = {
  nbsp: " ",
  mdash: "—",
  ndash: "–",
  hellip: "…",
  lsquo: "‘",
  rsquo: "’",
  ldquo: "“",
  rdquo: "”",
  trade: "™",
  reg: "®",
  copy: "©",
  deg: "°",
  times: "×",
  lt: "<",
  gt: ">",
  quot: '"',
  apos: "'"
}

// Answer bodies are HTML from DatoCMS, but JSON-LD is serialised with HTML
// entities and entities aren't decoded inside a <script>, so any markup left in
// here reaches consumers as literal "&lt;p&gt;" noise. Flatten to plain text.
const toPlainText = (html = "") =>
  html
    .replace(/<(br|\/p|\/div|\/li|\/h[1-6])[^>]*>/gi, " ")
    .replace(/<[^>]*>/g, "")
    .replace(/&([a-z]+);/gi, (match, name) => {
      const decoded = NAMED_ENTITIES[name.toLowerCase()]
      return decoded === undefined ? match : decoded
    })
    .replace(/&#(\d+);/g, (_, d) => String.fromCharCode(d))
    .replace(/&#x([0-9a-f]+);/gi, (_, h) =>
      String.fromCharCode(parseInt(h, 16))
    )
    .replace(/&amp;/g, "&")
    .replace(/\s+/g, " ")
    .trim()

// Only accordions that are genuinely Q&A get FAQPage markup — a heading that
// isn't a question is some other kind of content and shouldn't be declared as
// an FAQ.
const buildFaqSchema = (items = []) => {
  const questions = (items || [])
    .filter(item => item?.heading?.trim().endsWith("?"))
    .map(item => ({
      heading: item.heading.trim(),
      answer: toPlainText(item.text || "")
    }))
    .filter(item => item.answer)

  if (questions.length < 2) return null

  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: questions.map(item => ({
      "@type": "Question",
      name: item.heading,
      acceptedAnswer: {
        "@type": "Answer",
        text: item.answer
      }
    }))
  }
}

const BlogAccordion = ({ id, accordionItem, maxWidth = "inner" }) => {
  const faqSchema = buildFaqSchema(accordionItem)

  return (
    <Container maxWidth={maxWidth}>
      <Block gutters>
        <Accordion items={accordionItem} firstOpen id={id} />
        {faqSchema && <JsonLd item={faqSchema} />}
      </Block>
    </Container>
  )
}

export default BlogAccordion

export const query = graphql`
  fragment BlogAccordion on DatoCmsBlogAccordion {
    id
    __typename
    accordionItem {
      heading
      text
      id
    }
    padding
    model {
      apiKey
    }
  }
`
